import { useEffect, useState } from 'react';
import { fetchAgencyTasks } from './api';
import type { AgencyTaskView } from '@tsr/shared-types';
import { isTerminalAgencyTaskStatus } from '@tsr/shared-types';
import { agencyTaskStatusBadge, agencyTaskUrgencyLabel } from './agencyTaskUi';
import { outboundTargetLabel } from './requestsTabVocabulary';

/**
 * OUTBOUND tasks worklist (#379, Coordination Re-Arch Phase 3b-2a) — the 4th Coordination
 * sub-tab. Lists the agency tasks THIS agency (the PEOC) issued DOWN to named agencies
 * (requester-scoped on the server); a row opens the {kind:'agencyTask'} SidePanel detail.
 * Mirrors DroneRequestsPanel: a passive 30 s re-poll + an open-count badge that stays live
 * while another tab is active. The TARGET-side response inbox is 3b-2b.
 */

export const OUTBOUND_TASKS_POLL_MS = 30_000;

export function OutboundTasksPanel({
  onSelectTask,
  onActiveCountChange,
}: {
  onSelectTask?: (task: AgencyTaskView) => void;
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  const [tasks, setTasks] = useState<AgencyTaskView[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function reload(): Promise<void> {
      try {
        const list = await fetchAgencyTasks();
        if (!cancelled) setTasks(list);
      } catch {
        /* keep current */
      }
    }
    void reload();
    const id = setInterval(() => void reload(), OUTBOUND_TASKS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Report the OPEN (non-terminal) count up from a single pure effect.
  useEffect(() => {
    onActiveCountChange?.(tasks.filter((t) => !isTerminalAgencyTaskStatus(t.task_status)).length);
  }, [tasks, onActiveCountChange]);

  return (
    <div className="px-3 py-3" data-testid="outbound-tasks-panel">
      {/* #379 (agency-task outbound) — internal task number kept in the comment, off the
          operator surface (W6 raw-enum legibility, ledger §3.D). */}
      <p className="mb-2 text-[11px] leading-relaxed text-slate-400">
        Tasks your agency issued DOWN to a named agency (PEOC down-tasking). A row opens its
        detail; the tasked agency’s response is tracked here as it acknowledges and works it.
      </p>
      {tasks.length === 0 ? (
        <p className="text-[11px] text-slate-500">
          No outbound tasks yet. Use “Task agency” on a dispatch recommendation or an
          assistance request to issue one.
        </p>
      ) : (
        <ul className="space-y-2">
          {tasks.map((t) => {
            const badge = agencyTaskStatusBadge(t.task_status);
            return (
              <li
                key={t.id}
                className="rounded border border-slate-700/60 bg-slate-800/40 px-3 py-2"
                data-testid="agency-task-row"
              >
                <div className="flex items-start justify-between gap-2">
                  {onSelectTask ? (
                    <button
                      type="button"
                      onClick={() => onSelectTask(t)}
                      data-testid="agency-task-open"
                      className="min-w-0 text-left rounded -mx-1 px-1 hover:bg-slate-700/40"
                      title="Open the agency task detail"
                    >
                      <p className="text-sm font-medium truncate">
                        {outboundTargetLabel(t.target_agency_name)}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {t.task_number} · {agencyTaskUrgencyLabel(t.urgency)}
                      </p>
                    </button>
                  ) : (
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {outboundTargetLabel(t.target_agency_name)}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {t.task_number} · {agencyTaskUrgencyLabel(t.urgency)}
                      </p>
                    </div>
                  )}
                  <span
                    className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.cls}`}
                  >
                    {badge.label}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
